import { useCallback, useEffect, useRef, useState } from 'react';
import { Phone, Mail, Building2, ChevronRight, MessageCircle } from 'lucide-react';
import LeadQuickActionsSheet from './LeadQuickActionsSheet';
import type { MobileLead } from './types';

interface LeadCardProps {
  lead: MobileLead;
  onTap: (lead: MobileLead) => void;
  /**
   * Internal-app handlers — same contract as the desktop CRM. The mobile UI
   * never falls back to `tel:`/`wa.me`/`mailto:` so calls go through the
   * in-app SIP softphone, WhatsApp goes through the WhatsApp mini-chat
   * modal, and Email goes through the EmailComposeModal.
   */
  onCall: (phone: string, name: string, leadId: string) => void;
  onWhatsApp: (phone: string, name: string, leadId: string) => void;
  onEmail: (email: string, name: string, leadId: string) => void;
}

const priorityStyles: Record<MobileLead['priority'], string> = {
  urgent: 'bg-destructive/15 text-destructive',
  high: 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
  medium: 'bg-primary/15 text-primary',
  low: 'bg-muted text-muted-foreground',
};

function formatCurrency(value?: number) {
  if (typeof value !== 'number' || Number.isNaN(value)) return null;
  try {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    return `${Math.round(value)} €`;
  }
}

const LONG_PRESS_MS = 450;
const MOVE_THRESHOLD_PX = 10;

export default function LeadCard({ lead, onTap, onCall, onWhatsApp, onEmail }: LeadCardProps) {
  const fullName =
    [lead.contact_first_name, lead.contact_last_name].filter(Boolean).join(' ').trim() ||
    lead.title ||
    'Lead senza nome';

  const value = formatCurrency(lead.value);
  const phone = lead.contact_phone?.trim();
  const email = lead.contact_email?.trim();

  const [sheetOpen, setSheetOpen] = useState(false);

  // Long-press opens the quick-actions sheet; we suppress the trailing click
  // so the timeline modal doesn't also open underneath.
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressFiredRef = useRef(false);
  const startPosRef = useRef<{ x: number; y: number } | null>(null);

  const clearLongPressTimer = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  useEffect(() => clearLongPressTimer, [clearLongPressTimer]);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>) => {
      if (e.button !== undefined && e.button !== 0) return;
      longPressFiredRef.current = false;
      startPosRef.current = { x: e.clientX, y: e.clientY };
      clearLongPressTimer();
      longPressTimer.current = setTimeout(() => {
        longPressFiredRef.current = true;
        setSheetOpen(true);
        navigator.vibrate?.(15);
      }, LONG_PRESS_MS);
    },
    [clearLongPressTimer],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>) => {
      if (!startPosRef.current) return;
      const dx = e.clientX - startPosRef.current.x;
      const dy = e.clientY - startPosRef.current.y;
      if (Math.hypot(dx, dy) > MOVE_THRESHOLD_PX) {
        clearLongPressTimer();
      }
    },
    [clearLongPressTimer],
  );

  const handlePointerEnd = useCallback(() => {
    clearLongPressTimer();
    startPosRef.current = null;
  }, [clearLongPressTimer]);

  const handleClick = useCallback(() => {
    if (longPressFiredRef.current) {
      longPressFiredRef.current = false;
      return;
    }
    onTap(lead);
  }, [lead, onTap]);

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
  }, []);

  const stopRowTap = (e: React.MouseEvent | React.PointerEvent) => {
    e.stopPropagation();
  };

  return (
    <>
      <div
        className="rounded-xl border border-border bg-card overflow-hidden"
        data-testid={`mobile-lead-card-${lead.id}`}
      >
        <button
          type="button"
          onClick={handleClick}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerEnd}
          onPointerCancel={handlePointerEnd}
          onPointerLeave={handlePointerEnd}
          onContextMenu={handleContextMenu}
          className="w-full min-h-[44px] text-left active:bg-accent transition-colors p-4 flex items-start gap-3 select-none touch-manipulation"
          style={{ WebkitTouchCallout: 'none' }}
          aria-haspopup="dialog"
          data-testid={`mobile-lead-card-body-${lead.id}`}
        >
          <div className="flex-1 min-w-0 space-y-1.5">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-foreground truncate">{fullName}</span>
              <span
                className={`text-[11px] px-1.5 py-0.5 rounded-full font-medium ${priorityStyles[lead.priority]}`}
              >
                {lead.priority}
              </span>
            </div>

            {lead.company_name && (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Building2 className="w-3 h-3 shrink-0" />
                <span className="truncate">{lead.company_name}</span>
              </div>
            )}

            <div className="flex items-center justify-between pt-1">
              <div className="flex items-center gap-2 min-w-0">
                {lead.stage_name && (
                  <span
                    className="text-[11px] font-medium px-2 py-0.5 rounded-full truncate max-w-[140px]"
                    style={{
                      backgroundColor: lead.stage_color ? `${lead.stage_color}22` : undefined,
                      color: lead.stage_color || undefined,
                    }}
                  >
                    {lead.stage_name}
                  </span>
                )}
                {lead.campaign_name && (
                  <span className="text-[11px] text-muted-foreground truncate max-w-[120px]">
                    {lead.campaign_name}
                  </span>
                )}
              </div>
              {value && <span className="text-sm font-semibold text-foreground shrink-0 ml-2">{value}</span>}
            </div>
          </div>

          <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0 mt-1" />
        </button>

        {/* Quick actions: only render if we have at least one channel.
            These are buttons, not <a href="tel:|wa.me|mailto:"> — the mobile
            CRM uses the same in-app surfaces as the desktop CRM. */}
        {(phone || email) && (
          <div
            className="border-t border-border flex divide-x divide-border"
            data-testid={`mobile-lead-actions-${lead.id}`}
          >
            {phone && (
              <button
                type="button"
                onClick={(e) => {
                  stopRowTap(e);
                  onCall(phone, fullName, lead.id);
                }}
                className="flex-1 min-h-[44px] flex items-center justify-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-accent active:bg-accent/70 transition-colors"
                aria-label={`Chiama ${fullName}`}
                data-testid={`mobile-lead-call-${lead.id}`}
              >
                <Phone className="w-4 h-4" /> Chiama
              </button>
            )}
            {phone && (
              <button
                type="button"
                onClick={(e) => {
                  stopRowTap(e);
                  onWhatsApp(phone, fullName, lead.id);
                }}
                className="flex-1 min-h-[44px] flex items-center justify-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-accent active:bg-accent/70 transition-colors"
                aria-label={`Apri WhatsApp con ${fullName}`}
                data-testid={`mobile-lead-wa-${lead.id}`}
              >
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </button>
            )}
            {email && (
              <button
                type="button"
                onClick={(e) => {
                  stopRowTap(e);
                  onEmail(email, fullName, lead.id);
                }}
                className="flex-1 min-h-[44px] flex items-center justify-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-accent active:bg-accent/70 transition-colors"
                aria-label={`Email a ${fullName}`}
                data-testid={`mobile-lead-email-${lead.id}`}
              >
                <Mail className="w-4 h-4" /> Email
              </button>
            )}
          </div>
        )}
      </div>

      <LeadQuickActionsSheet
        lead={lead}
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        onOpenTimeline={onTap}
        onCall={onCall}
        onWhatsApp={onWhatsApp}
        onEmail={onEmail}
      />
    </>
  );
}
